import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";
import { driverIssueTableColumns } from "../../../data/FormColumns/Recruiting/driverIssueTableColumns";
import { DriverIssueDto } from "../../../interfaces/Recruiting/driverIssue";

const fetchDriverIssues = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/recruiting/driver-issues`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const DriverIssuesTable = () => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["driverIssues"],
    queryFn: () => fetchDriverIssues(token),
    enabled: !!token,
  });

  const filteredIssues = useMemo(() => {
    if (!data) return [];
    return (data as DriverIssueDto[]).filter((issue) =>
      [
        issue.driverName,
        issue.truckUnitNumber,
        issue.priority,
        issue.status,
        issue.contextOfConcern,
      ].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [data, searchTerm]);

  return (
    <Box className="incidents-table-container" height={900}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by driver, truck, priority, status, or context"
          sx={{ maxWidth: "100%", width: 400 }}
        />
      </Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Error: {(error as any).message}</Typography>
      ) : (
        <PersistedDataGrid
          tableName="driverIssues"
          rows={filteredIssues}
          columns={driverIssueTableColumns}
          compactViewConfig={{
            primaryField: "driverName",
            secondaryFields: ["truckUnitNumber", "dateOfIssueNotification"],
            detailFields: [
              "phoneNumber",
              "dispatcherName",
              "priority",
              "contextOfConcern",
              "concernAwareBy",
              "actionsTaken",
              "resolutionDate",
              "terminationComments",
              "resolutionComments",
            ],
            actionFields: ["update", "delete"],
            statusField: "status",
          }}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default DriverIssuesTable;
